import type { JoinCodeDoc } from '../../services/joinCodeService'
import type { TimelineElement } from '../../types/timelineElement'

export interface ClassOption {
  id: string
  title: string
  label: string
}

export const buildClassOptions = (elements: TimelineElement[]): ClassOption[] => {
  const topGoals = elements.filter(
    (el) => (el.parentId === null || el.parentId === '') && (el.type === 'GOAL' || !el.parentId),
  )
  const topGoalIds = new Set(topGoals.map((g) => g.id))
  const goalMap = new Map(topGoals.map((g) => [g.id, g]))

  const directChildren = elements.filter(
    (el) => el.parentId && topGoalIds.has(el.parentId),
  )

  const options: ClassOption[] = []

  for (const goal of topGoals) {
    options.push({
      id: goal.id,
      title: goal.title,
      label: goal.title,
    })
  }

  for (const child of directChildren) {
    const parentGoal = goalMap.get(child.parentId!)
    const label = parentGoal ? `${parentGoal.title} › ${child.title}` : child.title
    options.push({
      id: child.id,
      title: child.title,
      label,
    })
  }

  return options
}

const normalize = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ')

export interface ClassMatchResult {
  match?: ClassOption
  error?: 'NOT_FOUND' | 'AMBIGUOUS'
}

export const matchClass = (
  query: string,
  candidates: Array<{ id: string; title: string; label?: string }>,
): ClassMatchResult => {
  const normQuery = normalize(query)
  if (!normQuery) {
    return { error: 'NOT_FOUND' }
  }

  const matches = candidates.filter((c) => normalize(c.title) === normQuery)
  if (matches.length === 1) {
    return {
      match: {
        id: matches[0].id,
        title: matches[0].title,
        label: matches[0].label ?? matches[0].title,
      },
    }
  }

  if (matches.length > 1) {
    return { error: 'AMBIGUOUS' }
  }

  return { error: 'NOT_FOUND' }
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const PHONE_REGEX = /^\+[0-9]{8,15}$/

export interface MappedLearnerRow {
  mapped: {
    displayName: string
    isMinor: boolean
    dob?: string
    admissionNo?: string
    rollNo?: string
    nodeId?: string
    guardianEmail?: string
    guardianPhone?: string
  }
  errors: Record<string, string>
  isValid: boolean
}

export const mapCsvRowToLearner = (
  raw: Record<string, unknown>,
  kind: 'TEACHING' | 'TRAINING',
  classCandidates?: ClassOption[],
): MappedLearnerRow => {
  const errors: Record<string, string> = {}

  // Name
  const rawName = String(raw.name ?? raw.displayName ?? '').trim()
  if (!rawName) {
    errors.name = 'NAME_REQUIRED'
  }

  // Minor
  let isMinor = kind === 'TEACHING'
  const rawMinor = raw.minor !== undefined ? String(raw.minor).trim().toLowerCase() : ''
  if (rawMinor) {
    if (['yes', 'true', 'y', '1'].includes(rawMinor)) {
      isMinor = true
    } else if (['no', 'false', 'n', '0'].includes(rawMinor)) {
      isMinor = false
    } else {
      errors.minor = 'INVALID_MINOR'
    }
  }

  // DOB
  const dob = raw.dob ? String(raw.dob).trim() : undefined

  // Admission No
  const admissionNo = raw.admissionNo ? String(raw.admissionNo).trim() : undefined

  // Roll No
  const rollNo = raw.rollNo ? String(raw.rollNo).trim() : undefined

  // Class
  let nodeId: string | undefined
  const rawClass = raw.class !== undefined ? String(raw.class).trim() : ''
  if (rawClass && classCandidates && classCandidates.length > 0) {
    const classResult = matchClass(rawClass, classCandidates)
    if (classResult.match) {
      nodeId = classResult.match.id
    } else if (classResult.error === 'AMBIGUOUS') {
      errors.class = 'AMBIGUOUS'
    } else {
      errors.class = 'NOT_FOUND'
    }
  }

  // Guardian Email
  let guardianEmail: string | undefined
  const rawEmail = raw.guardianEmail !== undefined ? String(raw.guardianEmail).trim().toLowerCase() : ''
  if (rawEmail) {
    if (!EMAIL_REGEX.test(rawEmail)) {
      errors.guardianEmail = 'INVALID_EMAIL'
    } else {
      guardianEmail = rawEmail
    }
  }

  // Guardian Phone
  let guardianPhone: string | undefined
  const rawPhone = raw.guardianPhone !== undefined ? String(raw.guardianPhone).trim() : ''
  if (rawPhone) {
    const cleanedPhone = rawPhone.replace(/[\s-]/g, '')
    if (!PHONE_REGEX.test(cleanedPhone)) {
      errors.guardianPhone = 'INVALID_PHONE'
    } else {
      guardianPhone = cleanedPhone
    }
  }

  return {
    mapped: {
      displayName: rawName,
      isMinor,
      ...(dob ? { dob } : {}),
      ...(admissionNo ? { admissionNo } : {}),
      ...(rollNo ? { rollNo } : {}),
      ...(nodeId ? { nodeId } : {}),
      ...(guardianEmail ? { guardianEmail } : {}),
      ...(guardianPhone ? { guardianPhone } : {}),
    },
    errors,
    isValid: Object.keys(errors).length === 0,
  }
}

export { chunkArray } from '../JoinCodes/joinCodeUtils'

const getTimestampMillis = (ts: unknown): number => {
  if (!ts) return 0
  if (typeof (ts as { toMillis?: () => number }).toMillis === 'function') {
    return (ts as { toMillis: () => number }).toMillis()
  }
  if (typeof (ts as { seconds?: number }).seconds === 'number') {
    const s = (ts as { seconds: number; nanoseconds?: number }).seconds
    const ns = (ts as { nanoseconds?: number }).nanoseconds ?? 0
    return s * 1000 + ns / 1e6
  }
  if (ts instanceof Date) {
    return ts.getTime()
  }
  if (typeof ts === 'number') {
    return ts
  }
  return 0
}

export const mapLatestJoinCodesByLearnerId = (
  joinCodes: JoinCodeDoc[],
): Record<string, JoinCodeDoc> => {
  const result: Record<string, JoinCodeDoc> = {}

  for (const code of joinCodes) {
    const learnerId = code.prepared?.learnerId
    if (!learnerId) continue

    const existing = result[learnerId]
    if (!existing) {
      result[learnerId] = code
      continue
    }

    const currentCreatedAt = getTimestampMillis(code.createdAt)
    const existingCreatedAt = getTimestampMillis(existing.createdAt)

    if (currentCreatedAt > existingCreatedAt) {
      result[learnerId] = code
    }
  }

  return result
}
